import axios, { AxiosError, AxiosResponse } from "axios";
import { OBSUploadData, UploadRequestOption } from "@/types";
import * as Sentry from "@sentry/react";
import { FileUploadDomain } from "@/constant/file";

type UploadProgressEvent = Parameters<
  NonNullable<UploadRequestOption["onProgress"]>
>[0];

interface UploadRequestError extends Error {
  status: number;
  method: UploadRequestOption["method"];
  url: string;
}

function getError(option: UploadRequestOption, xhr: AxiosError) {
  let msg = `文件上传失败（code: ${xhr.status}），请稍后重试！`;
  try {
    // 解析 XML 数据
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(
      <string>xhr.response?.data || "",
      "text/xml",
    );
    const messageNode = xmlDoc.getElementsByTagName("Message")[0];
    if (messageNode.textContent) {
      msg = messageNode.textContent;
    }
  } catch (e) {}
  const err = new Error(msg) as UploadRequestError;
  err.status = <number>xhr.response?.status;
  err.method = option.method;
  err.url = option.action;
  return err;
}

function getBody(xhr: AxiosResponse) {
  const text = xhr.statusText || xhr.data;
  if (!text) {
    return text;
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

const upload = (
  option: UploadRequestOption,
  obsUploadData: OBSUploadData,
  done: () => void,
) => {
  const {
    file,
    onSuccess,
    onProgress,
    onError,
    action,
    headers,
    method,
    data,
  } = option;
  if (!data) {
    return;
  }
  const formData = new FormData();
  // 默认添加 obsUploadData，这样可以不用每个都传这部分数据
  Object.entries({ ...(data || {}), ...(obsUploadData || {}) }).forEach(
    ([key, value]) => {
      formData.append(key, value as string | Blob);
    },
  );
  formData.append("file", file);

  const originAction = action || (data.domain as string);
  /**
   * 直接用华为云的域名，不用 cdn 域名（img4 是cdn域名）
   */
  const url =
    originAction === "https://img4.yishouapp.com/"
      ? FileUploadDomain
      : originAction;

  axios({
    url,
    method: method || "post",
    headers: {
      "Content-Type": "multipart/form-data",
      ...(headers || {}),
    },
    data: formData,
    onUploadProgress: (e: UploadProgressEvent) => {
      if (e?.total) {
        e.percent = ((e.loaded as number) / e.total) * 100;
      }
      onProgress?.(e);
    },
  })
    .then((res) => {
      console.log("--file upload success--", res);
      onSuccess?.(getBody(res));
      done?.();
    })
    .catch((err) => {
      console.error("--file upload error--", JSON.stringify(err));
      // 主动上报图片上传错误
      try {
        // 解析 XML 数据
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
          <string>err.response?.data || "",
          "text/xml",
        );
        const codeNode = xmlDoc.getElementsByTagName("Code")[0];
        const messageNode = xmlDoc.getElementsByTagName("Message")[0];
        const requestIdNode = xmlDoc.getElementsByTagName("RequestId")[0];
        if (messageNode.textContent) {
          Sentry.captureMessage(
            JSON.stringify({
              code: codeNode.textContent,
              msg: messageNode.textContent,
              rqId: requestIdNode.textContent,
            }),
          );
        }
      } catch (e) {
        Sentry.captureMessage(`图片上传失败：${err.message || err.msg}`);
        Sentry.captureException(err);
      }
      onError?.(getError(option, err), getBody(err));
      done?.();
    });
};

export default upload;
